import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { ArrowLeft } from "lucide-react";

const LEGAL_LINKS = [
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "terms", label: "Terms & Conditions" },
  { slug: "grievance", label: "Grievance Redressal" },
  { slug: "refund", label: "Refund & Cancellation" },
  { slug: "disclaimer", label: "Disclaimer" },
];

export default function LegalPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(null);
    setNotFound(false);
    api.get(`/legal/${slug}`).then((r) => setPage(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="legal-not-found">
        <div className="badge-mono text-neutral-500 mb-3">404</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-3">Page not found</h1>
        <button onClick={() => nav("/")} className="text-[#0055FF] font-medium hover:underline">Go home →</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-black mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>
      <div className="grid md:grid-cols-[220px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="md:sticky md:top-24 h-fit">
          <div className="badge-mono text-neutral-500 mb-3">Legal</div>
          <nav className="space-y-1">
            {LEGAL_LINKS.map((l) => (
              <Link
                key={l.slug}
                to={`/legal/${l.slug}`}
                data-testid={`legal-nav-${l.slug}`}
                className={`block text-sm px-3 py-2 rounded-md transition-colors ${
                  slug === l.slug ? "bg-black text-white" : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="mt-8 text-[11px] text-neutral-400 font-mono-accent">
            {page?.updated_at && `Updated ${new Date(page.updated_at).toLocaleDateString()}`}
          </div>
        </aside>

        {/* Content */}
        <article data-testid={`legal-page-${slug}`}>
          {!page ? (
            <div className="text-neutral-400" data-testid="legal-loading">Loading…</div>
          ) : (
            <>
              <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-none mb-8">{page.title}</h1>
              <div className="prose prose-neutral max-w-none text-[15px] leading-relaxed">
                <ReactMarkdown
                  components={{
                    h2: ({ children }) => <h2 className="font-display text-2xl font-bold tracking-tight mt-10 mb-3">{children}</h2>,
                    h3: ({ children }) => <h3 className="font-display text-lg font-semibold tracking-tight mt-6 mb-2">{children}</h3>,
                    p: ({ children }) => <p className="text-neutral-700 mb-4 leading-[1.75]">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc pl-6 space-y-1.5 mb-4 text-neutral-700">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-6 space-y-1.5 mb-4 text-neutral-700">{children}</ol>,
                    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold text-neutral-900">{children}</strong>,
                    a: ({ children, href }) => <a href={href} className="text-[#0055FF] underline underline-offset-4 hover:text-[#0044CC]" target="_blank" rel="noopener noreferrer">{children}</a>,
                    code: ({ children }) => <code className="font-mono-accent text-sm bg-neutral-100 border border-black/8 rounded px-1.5 py-0.5">{children}</code>,
                  }}
                >
                  {page.body_md}
                </ReactMarkdown>
              </div>
            </>
          )}
        </article>
      </div>
    </div>
  );
}
